import { TrialFormStepFields } from '@/components/_rebrand/TrialForm/TrialPersonalInfoForm';
import { EMAIL_ERRORS } from '@/constants/email';
import { GTM_VARIABLES } from '@/constants/gtm';
import { HubspotFieldNames, HubspotFieldValidators, HubspotFormIDs } from '@/constants/hubspot';
import { OPTIMIZELY_EVENTS } from '@/constants/optimizely';
import { OneTrustContext } from '@/context/onetrust';
import { UserContext } from '@/context/user';
import frontBackend from '@/lib/api/front-backend';
import { SignupErrorResponse } from '@/lib/api/front-backend/signup';
import { getAppDomain } from '@/lib/api/front-backend/utils';
import attribution from '@/lib/attribution';
import { useCustomFormik } from '@/lib/formik';
import gtm from '@/lib/gtm';
import useEmailAvailability, { EmailAvailability } from '@/lib/hooks/useEmailAvailability';
import hubspot from '@/lib/hubspot';
import logging from '@/lib/logging';
import optimizely from '@/lib/optimizely';
import { isBrowser } from '@/lib/utils';
import { isWorkEmail, validatePassword } from '@/lib/validation';
import { AxiosError } from 'axios';
import { FormikProps } from 'formik';
import { useContext, useEffect, useState } from 'react';

export type GdprOptInState = {
  checked: boolean;
  onToggle: (checked: boolean) => void;
};

export type EmailState = {
  checking: boolean;
  isNotWorkEmail: boolean;
};

export type useTrialFormResult = {
  currentStep: number;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  showLoader: boolean;
  error: string;
  personalInfoFormik: FormikProps<TrialFormStepFields>;
  emailPasswordFormik: FormikProps<TrialFormStepFields>;
  confirmationCodeFormik: FormikProps<TrialFormStepFields & { code: string; password: string }>;
  gdprOptIn: GdprOptInState | null;
  emailState: EmailState;
};

export const useTrialForm = (): useTrialFormResult => {
  const { getTempUserData } = useContext(UserContext);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const urlParams = isBrowser() ? new URLSearchParams(window.location.search) : new URLSearchParams();
  // If override is true, proceed to self-service trial flow regardless of company size
  const overrideAlternateFlow = urlParams.get('override') === 'true';
  // If affiliate is true, proceed to self-service trial flow regardless of company size and replace '14-day' limit copy
  const affiliate = urlParams.get('affiliate');
  const hubSpotTrialType = overrideAlternateFlow ? 'Forced Trial' : affiliate ? 'Affiliate Signup' : '';
  const [emailStatus, setEmailStatus] = useEmailAvailability();
  // Values for passing between steps
  const [userData, setUserData] = useState<TrialFormStepFields | null>(null);
  const [error, setError] = useState<string>('');
  const { isGDPR } = useContext(OneTrustContext);
  const [marketingEmailOptIn, setMarketingEmailOptIn] = useState(isGDPR ? false : true);

  const handleMarketingOptInToggle = (checked: boolean) => {
    setMarketingEmailOptIn(checked);
    personalInfoFormik.setFieldValue(HubspotFieldNames.MarketingEmailOptOut, !checked);
  };

  useEffect(() => {
    if (affiliate) {
      attribution.setAffiliateFromQuery();
    }
  }, [affiliate]);

  // Fire optimizely event on email/password form view, wrap in useEffect to prevent firing on every render
  useEffect(() => {
    if (currentStep === 3) {
      optimizely.event(OPTIMIZELY_EVENTS.TRIAL_SIGNUP_SECOND_STEP);
      gtm.track('submit_form', {
        form_name: 'Trial Modal',
        form_content: 'Trial Modal',
      });
    }
  }, [currentStep]);

  // Fire optimizely event on confirmation code form view, wrap in useEffect to prevent firing on every render
  useEffect(() => {
    if (currentStep === 4) {
      optimizely.event(OPTIMIZELY_EVENTS.TRIAL_SIGNUP_THIRD_STEP);
    }
  }, [currentStep]);

  const personalInfoFormik = useCustomFormik<TrialFormStepFields>({
    initialValues: {
      [HubspotFieldNames.CompanyName]: '',
      [HubspotFieldNames.FirstName]: '',
      [HubspotFieldNames.Industry]: '',
      [HubspotFieldNames.JobTitle]: '',
      [HubspotFieldNames.LastName]: '',
      [HubspotFieldNames.Email]: '',
      [HubspotFieldNames.CompanySize]: '',
      [HubspotFieldNames.Phone]: '',
      // Marketing email opt in - the user must now actively opt in, so in GDPR regions the initial value is opted out.
      [HubspotFieldNames.MarketingEmailOptOut]: isGDPR ? true : false,
    },
    onSubmit: (values, actions) => {
      const companySize = parseInt(values[HubspotFieldNames.CompanySize]);
      /*
       * Trial type for over 50 is 'Customized Sales Trial' - indicating the redirection to demo flow
       * Trial type in case of override flag is 'Forced Trial'
       * Only the below 50 company size is 'Standard Trial'
       */

      const trialType = hubSpotTrialType
        ? hubSpotTrialType
        : companySize > 50
          ? 'Customized Sales Trial'
          : 'Standard Trial';

      hubspot
        .submitForm(HubspotFormIDs.GetStartedModalPartial, {
          [HubspotFieldNames.Affiliate]: attribution.getAffiliateFromCookie(),
          [HubspotFieldNames.ContentName]: 'Trial Modal',
          [HubspotFieldNames.Email]: values[HubspotFieldNames.Email],
          [HubspotFieldNames.FirstName]: values[HubspotFieldNames.FirstName],
          [HubspotFieldNames.LastName]: values[HubspotFieldNames.LastName],
          [HubspotFieldNames.CompanyName]: values[HubspotFieldNames.CompanyName],
          [HubspotFieldNames.JobTitle]: values[HubspotFieldNames.JobTitle],
          [HubspotFieldNames.CompanySize]: values[HubspotFieldNames.CompanySize],
          [HubspotFieldNames.Industry]: values[HubspotFieldNames.Industry],
          [HubspotFieldNames.Phone]: values[HubspotFieldNames.Phone],
          [HubspotFieldNames.MarketingEmailOptOut]: String(values[HubspotFieldNames.MarketingEmailOptOut]),
          [HubspotFieldNames.TrialType]: trialType,
        })
        .catch((e) => {
          logging.captureException(e);
        })
        .finally(() => {
          actions.setSubmitting(false);
          setUserData(values);
          // Proceed to self-service trial flow if company size is 50 or less or override is true or affiliate value is present
          if (parseInt(values.company_size__new_) <= 50 || overrideAlternateFlow || !!affiliate) {
            setCurrentStep(3);
          } else {
            // Redirect to alternate forced demo flow if company size is 51 or more and override is false
            setCurrentStep(2);
          }
        });
    },
    validate: (values) => {
      const errors = {};

      errors[HubspotFieldNames.Email] = HubspotFieldValidators[HubspotFieldNames.Email](
        values[HubspotFieldNames.Email],
        {
          required: true,
          allowPersonalEmail: true,
          allowSharedEmail: true,
        },
      );

      switch (emailStatus) {
        case EmailAvailability.Available:
          break;
        case EmailAvailability.Unavailable:
          errors[HubspotFieldNames.Email] = errors[HubspotFieldNames.Email] ?? EMAIL_ERRORS.EXISTS;
          break;
        case EmailAvailability.Error:
          errors[HubspotFieldNames.Email] = errors[HubspotFieldNames.Email] ?? EMAIL_ERRORS.ERROR;
          break;
        default:
          // Prevent submitting without showing an error if there isn't one set already
          errors[HubspotFieldNames.Email] = errors[HubspotFieldNames.Email] ?? '';
      }

      errors[HubspotFieldNames.FirstName] = HubspotFieldValidators[HubspotFieldNames.FirstName](
        values[HubspotFieldNames.FirstName],
        {
          required: true,
        },
      );

      errors[HubspotFieldNames.LastName] = HubspotFieldValidators[HubspotFieldNames.LastName](
        values[HubspotFieldNames.LastName],
        {
          required: true,
        },
      );

      errors[HubspotFieldNames.CompanyName] = HubspotFieldValidators[HubspotFieldNames.CompanyName](
        values[HubspotFieldNames.CompanyName],
        {
          required: true,
        },
      );

      errors[HubspotFieldNames.Industry] = HubspotFieldValidators[HubspotFieldNames.Industry](
        values[HubspotFieldNames.Industry],
        {
          required: true,
        },
      );

      errors[HubspotFieldNames.CompanySize] = HubspotFieldValidators[HubspotFieldNames.CompanySize](
        values[HubspotFieldNames.CompanySize],
        {
          required: true,
        },
      );

      errors[HubspotFieldNames.JobTitle] = HubspotFieldValidators[HubspotFieldNames.JobTitle](
        values[HubspotFieldNames.JobTitle],
        {
          required: true,
        },
      );

      errors[HubspotFieldNames.Phone] = HubspotFieldValidators[HubspotFieldNames.Phone](
        values[HubspotFieldNames.Phone],
        {
          required: true,
        },
      );

      if (Object.values(errors).every((val) => val === undefined)) return;

      return errors;
    },
    validateOnBlur: false,
  });

  useEffect(() => {
    const data = getTempUserData();

    Object.keys(data).forEach((key) => {
      personalInfoFormik.setFieldValue(key, data[key]);
    });
  }, []);

  // Email availability handling for personal info form
  useEffect(() => {
    setEmailStatus(personalInfoFormik.values[HubspotFieldNames.Email]);
  }, [personalInfoFormik.values[HubspotFieldNames.Email]]);

  useEffect(() => {
    if (emailStatus !== EmailAvailability.Untouched) {
      personalInfoFormik.setFieldTouched(HubspotFieldNames.Email, true, true);
    }
  }, [emailStatus]);

  const emailPasswordFormik = useCustomFormik<TrialFormStepFields>({
    enableReinitialize: true,
    initialValues: {
      password: '',
      [HubspotFieldNames.CompanyName]: userData?.[HubspotFieldNames.CompanyName] ?? '',
      [HubspotFieldNames.CompanySize]: userData?.[HubspotFieldNames.CompanySize] ?? '',
      [HubspotFieldNames.Email]: userData?.[HubspotFieldNames.Email] ?? '',
      [HubspotFieldNames.FirstName]: userData?.[HubspotFieldNames.FirstName] ?? '',
      [HubspotFieldNames.Industry]: userData?.[HubspotFieldNames.Industry] ?? '',
      [HubspotFieldNames.JobTitle]: userData?.[HubspotFieldNames.JobTitle] ?? '',
      [HubspotFieldNames.LastName]: userData?.[HubspotFieldNames.LastName] ?? '',
      [HubspotFieldNames.Phone]: userData?.[HubspotFieldNames.Phone] ?? '',
      [HubspotFieldNames.MarketingEmailOptOut]: userData?.[HubspotFieldNames.MarketingEmailOptOut] ?? false,
    },
    onSubmit: (values, actions) => {
      frontBackend
        .validateEmail({
          email: values[HubspotFieldNames.Email],
          password: values.password ?? '',
          affiliate: values[HubspotFieldNames.Affiliate],
          origin: window?.location?.href ?? '',
        })
        .then(() => {
          setUserData(values);
          setCurrentStep(4);
        })
        .catch((e) => {})
        .finally(() => {
          actions.setSubmitting(false);
          gtm.track('submit_form', {
            form_name: 'Trial Step 3',
            form_content: 'Trial Step 3',
          });
        });
    },
    validate: (values) => {
      const errors = {};

      errors[HubspotFieldNames.Email] = HubspotFieldValidators[HubspotFieldNames.Email](
        values[HubspotFieldNames.Email],
        {
          required: true,
          allowPersonalEmail: true,
          allowSharedEmail: true,
        },
      );

      switch (emailStatus) {
        case EmailAvailability.Available:
          break;
        case EmailAvailability.Unavailable:
          errors[HubspotFieldNames.Email] = errors[HubspotFieldNames.Email] ?? EMAIL_ERRORS.EXISTS;
          break;
        case EmailAvailability.Error:
          errors[HubspotFieldNames.Email] = errors[HubspotFieldNames.Email] ?? EMAIL_ERRORS.ERROR;
          break;
        default:
          // Prevent submitting without showing an error if there isn't one set already
          errors[HubspotFieldNames.Email] = errors[HubspotFieldNames.Email] ?? '';
      }

      errors['password'] = ((password) => {
        if (!password) {
          return 'Password is required';
        }

        if (!validatePassword(password)) {
          return '8 characters minimum, at least 1 uppercase letter and 1 number';
        }
      })(values['password']);

      errors[HubspotFieldNames.CompanySize] = HubspotFieldValidators[HubspotFieldNames.CompanySize](
        values[HubspotFieldNames.CompanySize],
        {
          required: true,
        },
      );

      if (Object.values(errors).every((val) => val === undefined)) return;

      return errors;
    },
    validateOnBlur: false,
  });

  const confirmationCodeFormik = useCustomFormik<TrialFormStepFields & { code: string; password: string }>({
    enableReinitialize: true,
    initialValues: {
      code: '',
      password: userData?.['password'] ?? '',
      [HubspotFieldNames.CompanyName]: userData?.[HubspotFieldNames.CompanyName] ?? '',
      [HubspotFieldNames.CompanySize]: userData?.[HubspotFieldNames.CompanySize] ?? '',
      [HubspotFieldNames.Email]: userData?.[HubspotFieldNames.Email] ?? '',
      [HubspotFieldNames.FirstName]: userData?.[HubspotFieldNames.FirstName] ?? '',
      [HubspotFieldNames.Industry]: userData?.[HubspotFieldNames.Industry] ?? '',
      [HubspotFieldNames.JobTitle]: userData?.[HubspotFieldNames.JobTitle] ?? '',
      [HubspotFieldNames.LastName]: userData?.[HubspotFieldNames.LastName] ?? '',
      [HubspotFieldNames.Phone]: userData?.[HubspotFieldNames.Phone] ?? '',
      [HubspotFieldNames.MarketingEmailOptOut]: userData?.[HubspotFieldNames.MarketingEmailOptOut] ?? false,
    },
    onSubmit: (values, actions) => {
      setError('');

      const timeoutId = setTimeout(() => {
        setShowLoader(true);
      }, 2000);

      const affiliate = attribution.getAffiliateFromCookie() || '';

      frontBackend
        .signup({
          affiliate: affiliate,
          code: values.code,
          company: values[HubspotFieldNames.CompanyName],
          email: values[HubspotFieldNames.Email],
          family_name: values[HubspotFieldNames.LastName],
          given_name: values[HubspotFieldNames.FirstName],
          industry: values[HubspotFieldNames.Industry],
          origin: window?.location?.href ?? '',
          password: values.password,
          size: values[HubspotFieldNames.CompanySize],
        })
        .then(({ data }) => {
          // TODO re-add autojoin logic
          setShowLoader(true);
          const props = {
            [GTM_VARIABLES.USER_EMAIL]: values[HubspotFieldNames.Email],
            [GTM_VARIABLES.USER_FIRST_NAME]: values[HubspotFieldNames.FirstName],
            [GTM_VARIABLES.USER_LAST_NAME]: values[HubspotFieldNames.LastName],
            affiliate,
            company: values[HubspotFieldNames.CompanyName],
            gclid: attribution.getAttributionFromCookie().gclid,
            industry: values[HubspotFieldNames.Industry],
            label: window.btoa(unescape(encodeURIComponent(data.first_admin_id ?? data.id ?? ''))),
            teammate_id: data.first_admin_id,
            marketing_email_opt_out: values[HubspotFieldNames.MarketingEmailOptOut],
          };

          hubspot
            .submitForm(HubspotFormIDs.TrialSignup, {
              [HubspotFieldNames.Affiliate]: affiliate,
              [HubspotFieldNames.ContentName]: 'Trial Signup',
              [HubspotFieldNames.Email]: values[HubspotFieldNames.Email],
              [HubspotFieldNames.FirstName]: values[HubspotFieldNames.FirstName],
              [HubspotFieldNames.LastName]: values[HubspotFieldNames.LastName],
              [HubspotFieldNames.CompanyName]: values[HubspotFieldNames.CompanyName],
              [HubspotFieldNames.JobTitle]: values[HubspotFieldNames.JobTitle],
              [HubspotFieldNames.CompanySize]: values[HubspotFieldNames.CompanySize],
              [HubspotFieldNames.Industry]: values[HubspotFieldNames.Industry],
              [HubspotFieldNames.Phone]: values[HubspotFieldNames.Phone],
              [HubspotFieldNames.MarketingEmailOptOut]: String(values[HubspotFieldNames.MarketingEmailOptOut]),
              [HubspotFieldNames.TrialType]: hubSpotTrialType ? hubSpotTrialType : 'Standard Trial',
            })
            .catch((e) => {
              logging.captureException(e);
            })
            .finally(() => {
              // If we have an alternate hubSpotTrialType value, fire corresponding events
              hubSpotTrialType ? gtm.trackForcedTrialSignup(hubSpotTrialType, props) : gtm.trackTrialSignup(props);

              optimizely.event(OPTIMIZELY_EVENTS.TRIAL_SIGNUP);
              setTimeout(() => {
                window.location.href = data.first_login_url ?? getAppDomain();
              }, 250);
            });
        })
        .catch((error: AxiosError<SignupErrorResponse>) => {
          clearTimeout(timeoutId);
          setShowLoader(false);

          logging.captureException(error);

          if (error.response?.data?.code === 'bad_validation_code') {
            actions.setFieldError('code', 'Incorrect validation code');
          } else if (error.response?.status === 429) {
            setError('Too many attempts, please try again later');
          } else {
            setError('An unexpected error occurred, please refresh and try again');
          }
        })
        .finally(() => {
          actions.setSubmitting(false);
        });
    },
    validate: (values) => {
      const errors = {};

      errors[HubspotFieldNames.FirstName] = HubspotFieldValidators[HubspotFieldNames.FirstName](
        values[HubspotFieldNames.FirstName],
        {
          required: true,
        },
      );

      errors[HubspotFieldNames.LastName] = HubspotFieldValidators[HubspotFieldNames.LastName](
        values[HubspotFieldNames.LastName],
        {
          required: true,
        },
      );

      errors[HubspotFieldNames.CompanyName] = HubspotFieldValidators[HubspotFieldNames.CompanyName](
        values[HubspotFieldNames.CompanyName],
        {
          required: true,
        },
      );

      errors[HubspotFieldNames.Industry] = HubspotFieldValidators[HubspotFieldNames.Industry](
        values[HubspotFieldNames.Industry],
        {
          required: true,
        },
      );

      errors[HubspotFieldNames.JobTitle] = HubspotFieldValidators[HubspotFieldNames.JobTitle](
        values[HubspotFieldNames.JobTitle],
        {
          required: true,
        },
      );

      errors['code'] = ((code) => {
        if (!code) {
          return 'Please enter the code sent to your email';
        }

        if (code.length !== 6) {
          return 'The code is 6 digits long';
        }
      })(values['code']);

      if (Object.values(errors).every((val) => val === undefined)) return;

      return errors;
    },
    validateOnBlur: false,
  });

  /*
   * Event and form submission for Managed Trial Confirmation step.
   * Fire optimizely event on form view, wrap in useEffect to prevent firing on every render
   */
  useEffect(() => {
    if (currentStep === 2) {
      optimizely.event(OPTIMIZELY_EVENTS.CUSTOMIZED_TRIAL_SUCCESS);
      gtm.trackCustomSalesTrialSignup();
      const affiliate = attribution.getAffiliateFromCookie() || '';

      hubspot
        .submitForm(HubspotFormIDs.TrialSignup, {
          [HubspotFieldNames.Affiliate]: affiliate,
          [HubspotFieldNames.ContentName]: 'Trial Signup',
          [HubspotFieldNames.Email]: userData?.[HubspotFieldNames.Email] ?? '',
          [HubspotFieldNames.FirstName]: userData?.[HubspotFieldNames.FirstName] ?? '',
          [HubspotFieldNames.LastName]: userData?.[HubspotFieldNames.LastName] ?? '',
          [HubspotFieldNames.CompanyName]: userData?.[HubspotFieldNames.CompanyName] ?? '',
          [HubspotFieldNames.JobTitle]: userData?.[HubspotFieldNames.JobTitle] ?? '',
          [HubspotFieldNames.CompanySize]: userData?.[HubspotFieldNames.CompanySize] ?? '',
          [HubspotFieldNames.Industry]: userData?.[HubspotFieldNames.Industry] ?? '',
          [HubspotFieldNames.Phone]: userData?.[HubspotFieldNames.Phone] ?? '',
          [HubspotFieldNames.MarketingEmailOptOut]: String(userData?.[HubspotFieldNames.MarketingEmailOptOut] ?? false),
          [HubspotFieldNames.TrialType]: 'Customized Sales Trial',
        })
        .catch((e) => {
          logging.captureException(e);
        });
    }
  }, [userData, currentStep]);

  return {
    currentStep,
    setCurrentStep,
    showLoader,
    emailState: {
      checking: emailStatus === EmailAvailability.Checking,
      isNotWorkEmail: !isWorkEmail(personalInfoFormik.values[HubspotFieldNames.Email]),
    },
    error,
    personalInfoFormik,
    emailPasswordFormik,
    confirmationCodeFormik,
    gdprOptIn: isGDPR ? { checked: marketingEmailOptIn, onToggle: handleMarketingOptInToggle } : null,
  };
};
